import { Task } from '../types';

/**
 * Does this participant see this task?
 *
 * Assignment lives on the task as `assignedParticipantIds` (composite
 * participant doc IDs, `${uid}-${projectId}`). The participant app used to
 * ignore it entirely and show every active task in the project to everyone,
 * so "assign to these five people" changed nothing about what was on screen.
 *
 * Kept as a pure function because getting it wrong in either direction is
 * costly: too strict and a participant's study looks empty, too loose and
 * they see work that isn't theirs.
 */
export const isTaskVisibleToParticipant = (
  task: Task,
  participantId: string,
  opts: {
    /** Task IDs this participant has already submitted against. */
    submittedTaskIds: Set<string>;
    /** Researcher previewing the participant app — show the whole study. */
    isAdminPreview?: boolean;
  },
): boolean => {
  if (task.status !== 'active') return false;
  if (opts.isAdminPreview) return true;
  // Already submitted: stay visible, so unassigning someone afterwards doesn't
  // vanish work they've already done along with the history UI that reads it.
  if (opts.submittedTaskIds.has(task.id)) return true;
  // Saved with nobody assigned: held back until someone is. Read from the
  // explicit flag, never inferred from an empty list — 7 of 10 live tasks at
  // the time of writing had no list at all and were running studies.
  if (task.isDraft) return false;
  // No list and not flagged: predates the assignment picker (or was seeded),
  // so it belongs to the whole project rather than to nobody.
  if (!task.assignedParticipantIds?.length) return true;
  return task.assignedParticipantIds.includes(participantId);
};
